import { useState } from 'react'
import { Chip, Button, Grid } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import clsx from 'clsx'

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from 'components/Table'
import {
  MenuButton,
  OrganizationUsersForm,
  OrganizationUserRemoveDialog,
} from 'components'

import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import useStyles from './styles'
import constants from 'constants/index'
import helpers from 'helpers'

const OrganizationUsersTable = ({
  organizationUsers,
  refresh,
  showUser = false,
  showOrganization = false,
  isOrganizationUsersLoading,
}) => {
  const [currentOrganizationUser, setCurrentOrganizationUser] = useState()
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const classes = useStyles()
  const snackbar = useSnackbar()

  const { STATUS } = constants.organizationUsers

  const { OBSERVER_ADVISOR_AUTHOR_PROFILE_ID, RESPONSIBLE_DPO_PROFILE_ID } =
    constants.profile

  const handleEdit = (organizationUser) => {
    setCurrentOrganizationUser(organizationUser)
    setOpenEditDialog(true)
  }

  const handleChangeStatus = async (organizationUser) => {
    try {
      setIsLoading(true)
      await service.backoffice.organizationUsers.changeStatus({
        organizationUserId: organizationUser?.id,
        organizationId: organizationUser?.organization?.id,
        status: defineOrganizationUserStatus(organizationUser),
      })
      refresh()
      snackbar.open({
        message: 'Status alterado com sucesso!',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error ||
            'Aconteceu um erro, tente novamente mais tarde!',
        ),
        variant: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemove = (organizationUser) => {
    setCurrentOrganizationUser(organizationUser)
    setOpenRemoveDialog(true)
  }

  const defineOrganizationUserStatus = (organizationUser) => {
    if (organizationUser?.status === STATUS.ACTIVE) return STATUS.INACTIVE

    return STATUS.ACTIVE
  }

  const chipLabel = (organizationUser) => {
    if (organizationUser?.status === STATUS.PENDING) return 'Pendente'
    if (organizationUser?.status === STATUS.INACTIVE) return 'Inativo'

    return 'Ativo'
  }

  const disabledEdit = (organizationUser) =>
    [OBSERVER_ADVISOR_AUTHOR_PROFILE_ID, RESPONSIBLE_DPO_PROFILE_ID].includes(
      organizationUser?.profile?.id,
    )

  return isLoading || isOrganizationUsersLoading ? (
    <Grid container>
      <Grid item xs={12}>
        <Skeleton variant="rect" height={55} />
      </Grid>
    </Grid>
  ) : (
    <>
      <Table
        emptyMessage={`Não há ${
          showUser && !showOrganization ? 'usuários' : 'grupos advisor'
        } vinculados`}
      >
        <TableHead>
          <TableRow>
            <TableCell width="10%">ID</TableCell>
            {showUser && <TableCell width="25%">Usuário</TableCell>}
            {showOrganization && (
              <TableCell width="35%">Grupo Advisor</TableCell>
            )}
            <TableCell width="30%">Perfil</TableCell>
            {showOrganization && <TableCell width="15%">Tipo</TableCell>}
            <TableCell width="15%">Status</TableCell>
            <TableCell width="10%" align="right">
              Ações
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {organizationUsers?.map((organizationUser, index) => (
            <TableRow key={organizationUser?.id || index} hover>
              <TableCell disableTooltip>{organizationUser?.id}</TableCell>
              {showUser && (
                <TableCell>{organizationUser?.user?.name}</TableCell>
              )}
              {showOrganization && (
                <TableCell>{organizationUser?.organization?.name}</TableCell>
              )}
              <TableCell>{organizationUser?.profile?.name}</TableCell>

              {showOrganization && (
                <TableCell>
                  {
                    constants.organizations.KIND_LABEL[
                      organizationUser?.organization?.kind
                    ]
                  }
                </TableCell>
              )}
              <TableCell disableTooltip>
                <Chip
                  className={clsx(classes.root, {
                    [classes.active]:
                      organizationUser?.status === STATUS.ACTIVE,
                    [classes.inactive]:
                      organizationUser?.status === STATUS.INACTIVE,
                    [classes.pending]:
                      organizationUser?.status === STATUS.PENDING,
                  })}
                  align="center"
                  size="small"
                  label={chipLabel(organizationUser)}
                />
              </TableCell>
              <TableCell disableTooltip align="right">
                <MenuButton>
                  <Button
                    onClick={() => handleEdit(organizationUser)}
                    fullWidth
                    size="small"
                    disabled={disabledEdit(organizationUser)}
                  >
                    Editar
                  </Button>
                  {organizationUser?.status !== STATUS.PENDING && (
                    <Button
                      onClick={() => handleChangeStatus(organizationUser)}
                      fullWidth
                      size="small"
                      disabled={isLoading}
                    >
                      {organizationUser?.status === STATUS.ACTIVE
                        ? 'Inativar'
                        : 'Ativar'}
                    </Button>
                  )}
                  <Button
                    onClick={() => handleRemove(organizationUser)}
                    fullWidth
                    size="small"
                  >
                    Excluir
                  </Button>
                </MenuButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {currentOrganizationUser && currentOrganizationUser?.id && (
        <>
          <OrganizationUsersForm
            key={currentOrganizationUser?.organization?.id}
            setOpen={setOpenEditDialog}
            refresh={refresh}
            open={openEditDialog}
            currentOrganizationUser={currentOrganizationUser}
            organization={currentOrganizationUser?.organization}
            isEdit
          />
          <OrganizationUserRemoveDialog
            currentOrganizationUser={currentOrganizationUser}
            open={openRemoveDialog}
            setOpen={setOpenRemoveDialog}
            refresh={refresh}
          />
        </>
      )}
    </>
  )
}

export default OrganizationUsersTable
